import Spacer from "components/spacer/Spacer";
import { memo } from "react";
import { Container, Nav, Navbar as ReactNavbar } from "react-bootstrap";
import styled from "styled-components";
import SmartLink from "components/smartLink/SmartLink";
import LanguageSelector from "components/languageSelector/LanguageSelector";
import { useAppSelector } from "hooks";
import { languageSelector } from "selectors/uiSelectors";
import { localizePath } from "utility/languageUtility";
import { useTranslation } from "react-i18next";

const StyledNavbar = styled(ReactNavbar)`
  background: #28c604;
  padding: 0;
  min-height: auto;

  /* Override Bootstrap's lg breakpoint (992px) to use 700px instead */
  &.navbar-expand-lg .navbar-collapse {
    display: none;

    @media only screen and (min-width: 700px) {
      display: flex !important;
      flex-basis: auto;
    }

    &.show {
      display: block;

      @media only screen and (min-width: 700px) {
        display: flex !important;
      }
    }
  }

  &.navbar-expand-lg .navbar-toggler {
    display: block;

    @media only screen and (min-width: 700px) {
      display: none !important;
    }
  }

  &.navbar-expand-lg .navbar-nav {
    flex-direction: column;

    @media only screen and (min-width: 700px) {
      flex-direction: row !important;
    }
  }
`;

const StyledNav = styled(ReactNavbar.Collapse)`
  /* Show on desktop */
  @media only screen and (min-width: 700px) {
    justify-content: space-between !important;
  }

  /* Show when toggled open on mobile */
  &.show {
    .navbar-nav {
      padding-top: 10px;
      padding-bottom: 10px;
      flex: 1;
    }
  }
`;

const StyledNavbarToggle = styled(ReactNavbar.Toggle)`
  margin-left: auto;
  border: 0 !important;
  box-shadow: none !important;

  > span {
    background-image: url("data:image/svg+xml,<svg viewBox='0 0 30 30' xmlns='http://www.w3.org/2000/svg'><path stroke='rgba(255,255,255, 1)' stroke-width='2' stroke-linecap='round' stroke-miterlimit='10' d='M4 7h22M4 15h22M4 23h22'/></svg>");
  }
`;

const StyledLink = styled(SmartLink)`
  color: white;
  padding: 5px 10px;
  align-items: center;
  display: flex;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const MobileOnlyLink = styled(SmartLink)`
  color: white;
  padding: 5px 10px;
  align-items: center;
  display: none;
  text-decoration: none;

  /* Show on mobile */
  @media only screen and (max-width: 699px) {
    display: flex;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const ExternalLink = styled.a`
  color: white;
  padding: 5px 10px;
  align-items: center;
  display: none;
  text-decoration: none;

  /* Show on mobile */
  @media only screen and (max-width: 699px) {
    display: flex;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const MobileOnlySpacer = styled(Spacer)`
  display: none;

  /* Show on mobile */
  @media only screen and (max-width: 699px) {
    display: block;
    margin: 3px 0;
  }
`;

const NavSpacer = styled(Spacer)`
  /* Reduce spacing on mobile to match other links */
  @media only screen and (max-width: 699px) {
    margin: 3px 0;
  }
`;

const StyledContainer = styled(Container)`
  padding: 0;
  max-width: 1200px;
  display: flex;
  align-items: center;
`;

const LanguageNav = styled.div`
  display: flex;
  align-items: center;

  @media only screen and (min-width: 700px) {
    margin-left: auto;
    order: 1;
  }
`;

const Navbar = () => {
  const language = useAppSelector(languageSelector);
  const lp = (path: string) => localizePath(path, language);
  const { t } = useTranslation("common");

  return (
    <StyledNavbar expand="lg" data-bs-theme="dark">
      <StyledContainer>
        <LanguageNav>
          <LanguageSelector />
        </LanguageNav>
        <StyledNavbarToggle aria-controls="responsive-navbar-nav"></StyledNavbarToggle>
        <StyledNav id="responsive-navbar-nav">
          <Nav className="d-flex">
            <StyledLink to={lp("/")}>{t("nav.search")}</StyledLink>
            <NavSpacer color="white" />
            <StyledLink to={lp("/available-domains")}>
              {t("nav.availableDomains")}
            </StyledLink>
            <NavSpacer color="white" />
            <StyledLink to={lp("/expired-domains")}>
              {t("nav.expiredDomains")}
            </StyledLink>
            <MobileOnlySpacer color="white" />
            <MobileOnlyLink to={lp("/terms-and-conditions")}>
              {t("nav.termsAndConditions")}
            </MobileOnlyLink>
            <MobileOnlySpacer color="white" />
            <MobileOnlyLink to={lp("/privacy-policy")}>
              {t("nav.privacyPolicy")}
            </MobileOnlyLink>
            <MobileOnlySpacer color="white" />
            <MobileOnlyLink to={lp("/contact")}>
              {t("nav.contact")}
            </MobileOnlyLink>
            <MobileOnlySpacer color="white" />
            <ExternalLink
              href="https://massgenerate.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              MassGenerate
            </ExternalLink>
            <MobileOnlySpacer color="white" />
            <ExternalLink
              href="https://toolweaver.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              ToolWeaver
            </ExternalLink>
          </Nav>
        </StyledNav>
      </StyledContainer>
    </StyledNavbar>
  );
};

export default memo(Navbar);
